import type { GridApiPremium } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../api/biApi";
import type { WorkbookRunsProgress } from "../../../../api/types/workbookRuns.types";
import { getProcessingRowIds } from "../runs/workbookRunUtils";

const FETCH_INTERVAL_MS = 3000;

const getRunsProgress = withErrorHandling(biApi.getRunsProgress);

const useWorkbookRunsPolling = (
  apiRef: RefObject<GridApiPremium | null>,
  onDataLoaded: (data: WorkbookRunsProgress) => void
) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const pollRunsProgress = useCallback(async () => {
    const operationIdsInProgress = getProcessingRowIds(apiRef);
    if (operationIdsInProgress.length === 0 || abortControllerRef.current) {
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const [data, error] = await getRunsProgress(operationIdsInProgress, abortController.signal);
    abortControllerRef.current = null;

    if (abortController.signal.aborted) {
      return;
    }

    if (error || !data) {
      logError(error, "[useWorkbookRunsPolling] getRunsProgress");
      return;
    }
    onDataLoaded(data);
  }, [apiRef, onDataLoaded]);

  useSetInterval(pollRunsProgress, FETCH_INTERVAL_MS);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);
};

export default useWorkbookRunsPolling;
